/**
 * Base Analytics Adapter
 * Infrastructure layer - Base class for all analytics adapters
 * Provides common functionality and transformation patterns
 */

import { Event, type EventProperties } from '../../domain/models/Event';
import type { EventTracker } from '../../domain/interfaces/EventTracker';
import { getCommonClientAttributes } from '../../utils/commonAttributes';

/**
 * Base Adapter
 * Abstract base class that all analytics adapters should extend
 * Handles domain event to service-specific DTO transformation
 */
export abstract class BaseAdapter implements EventTracker {
  protected initialized = false;

  /**
   * Track a custom event
   * Transforms domain event to service-specific format
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void | Promise<void> {
    // Get common attributes (page name, URL params, user info)
    const commonAttributes = getCommonClientAttributes();
    
    // Merge common attributes with provided properties
    // Provided properties take precedence over common attributes
    const mergedProperties: EventProperties = {
      ...commonAttributes,
      ...(properties || {}),
    } as EventProperties;
    
    // Create domain event from merged parameters
    const event = new Event(eventName, mergedProperties);
    
    // Transform to service-specific DTO and track
    const dto = this.transformEventToDTO(event);
    this.trackEventDTO(dto);
  }

  /**
   * Transform domain event to service-specific DTO
   * Each adapter must implement this to convert domain events to their format
   */
  protected abstract transformEventToDTO(event: Event): unknown;

  /**
   * Track event using service-specific DTO
   * Each adapter must implement this to send events to their service
   */
  protected abstract trackEventDTO(dto: unknown): void | Promise<void>;

  /**
   * Initialize the tracker (if needed)
   */
  abstract initialize?(): void | Promise<void>;
}

